import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { ClientService } from 'src/app/service/client.service';
import { PlanoService } from 'src/app/service/plano.service';
import { RelatorioService } from 'src/app/service/relatorio.service';
import { ViaCepService } from 'src/app/service/viaCep.service';
import { Utils } from 'src/app/util/utils';
import { CidadeService } from 'src/app/service/cidade.service';
import { EstadosBrasil, NomesEstadosBrasil } from 'src/app/enum/estadosBrasil.enum';
import { Plano } from '../plano/plano';
import { Client, ClientDocumento } from './client';
import { SegmentoService } from 'src/app/service/segmento.service';
import { Segmento } from '../segmento/segmento';
import { ColaboradorService } from 'src/app/service/colaborador.service';
import { Colaborador } from '../colaborador/colaborador';

@Component({
  selector: 'app-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client.component.scss'],
})
export class ClientFormComponent implements OnInit {

  formulario: FormGroup;
  cidades: String[] = [];
  seller: string;
  estados = Object.values(EstadosBrasil);
  importadoWowve = false;
  plano: string;
  ativo = false;
  carregando = false;
  segmentos: Segmento[] = [];
  clientesPagadores: Client[] = [];
  planosPorSegmento: { [seg: string]: Plano[] } = {};
  colaboradores: Colaborador[] = [];
  documentos: ClientDocumento[] = [];
  enviandoDocumento = false;

  get clienteId(): number | null {
    return this.formulario?.get('id')?.value || null;
  }

  /** Cliente já salvo com tenant: bloqueia edição/regeneração. */
  get tenantIdBloqueado(): boolean {
    const id = this.formulario?.get('id')?.value;
    const tenant = this.formulario?.get('tenantId')?.value;
    return !!id && !!tenant && String(tenant).trim().length > 0;
  }

  constructor(
    private clientService: ClientService,
    private formBilder: FormBuilder,
    private snackBar: MatSnackBar,
    private planoService: PlanoService,
    private viaCepService: ViaCepService,
    private utils: Utils,
    private cidadeService: CidadeService,
    private route: ActivatedRoute,
    private router: Router,
    private segmentoService: SegmentoService,
    private colaboradorService: ColaboradorService,
    private relatorioService: RelatorioService,
  ) { }

  ngOnInit(): void {
    this.montarFormulario();
    this.carregarSegmentos();
    this.carregarClientesPagadores();
    this.carregarColaboradores();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.carregar(+id);
    }
  }

  carregarSegmentos(): void {
    this.segmentoService.listarAtivos().subscribe((response) => {
      this.segmentos = response;
      this.recarregarPlanosSegmentos();
    });
  }

  carregarClientesPagadores(): void {
    this.clientService.listarCliente().subscribe((response) => {
      this.clientesPagadores = response || [];
    });
  }

  carregarColaboradores(): void {
    this.colaboradorService.listarAtivos().subscribe((response) => {
      this.colaboradores = response || [];
    });
  }

  get clientesPagadoresFiltrados(): Client[] {
    const idAtual = this.formulario?.get('id')?.value;
    return this.clientesPagadores.filter(c => c.id !== idAtual);
  }

  carregar(id: number) {
    this.carregando = true;
    this.clientService.findClientById(id).subscribe((response) => {
      this.formulario.controls.id.setValue(id);
      this.formulario.controls.nomeFantasia.setValue(response.nomeFantasia);
      this.formulario.controls.address.setValue(response.address);
      this.formulario.controls.block.setValue(response.block);
      this.formulario.controls.batch.setValue(response.batch);
      this.formulario.controls.telephone.setValue(response.telephone);
      this.formulario.controls.complement.setValue(response.complement);
      this.formulario.controls.observation.setValue(response.observation);
      this.formulario.controls.plano.setValue(response.plano);
      this.formulario.controls.bairro.setValue(response.bairro);
      this.formulario.controls.cidade.setValue(response.city);
      this.seller = response.seller;
      this.formulario.controls.documento.setValue(response.documento);
      this.formulario.controls.razaoSocial.setValue(response.razaoSocial);
      this.formulario.controls.ativo.setValue(response.ativo);
      this.formulario.controls.whatsapp.setValue(response.whatsapp);
      this.formulario.controls.email.setValue(response.email);
      this.formulario.controls.cep.setValue(response.cep);
      this.formulario.controls.estado.setValue(response.estado);
      this.formulario.controls.pessoaContato.setValue(response.pessoaContato);
      this.formulario.controls.contato.setValue(response.contato);
      this.formulario.controls.carencia.setValue(response.carencia);
      this.formulario.controls.diaVencimento.setValue(response.diaVencimento);
      this.formulario.controls.tenantId.setValue(response.tenantId);
      this.formulario.controls.segmentos.setValue(response.segmentos || []);
      this.sincronizarPlanosSegmento(response.segmentos || [], response.planosSegmento || [], {
        plano: response.plano,
        planoId: response.planoId,
        diaVencimento: response.diaVencimento,
        carencia: response.carencia,
      });
      this.formulario.controls.clientePagadorId.setValue(response.clientePagadorId || null);
      this.importadoWowve = response.importadoWowve;
      this.plano = response.plano;
      this.ativo = response.ativo;
      if (response.estado) {
        this.buscarMunicipios(this.buscarSiglaEstadoInformado(response.estado));
      }
      this.carregarDocumentos(id);
      this.carregando = false;
    }, () => {
      this.carregando = false;
      this.snackBar.open('Cliente não encontrado.', 'ERRO', { duration: 2500 });
      this.voltarConsulta();
    });
  }

  submit() {
    const formValues = this.formulario.value;
    const principal = (formValues.planosSegmento || [])[0];
    const client: Client = new Client(
      formValues.id,
      formValues.nomeFantasia,
      formValues.address,
      formValues.block,
      formValues.batch,
      formValues.telephone,
      formValues.complement,
      formValues.observation,
      formValues.bairro,
      formValues.cidade,
      this.seller,
      formValues.documento,
      principal?.plano ?? null,
      principal?.planoId ?? null,
      formValues.razaoSocial,
      formValues.ativo,
      formValues.whatsapp,
      formValues.email,
      formValues.cep,
      formValues.estado,
      formValues.bairro,
      formValues.pessoaContato,
      formValues.contato,
      formValues.carencia,
      formValues.diaVencimento,
      formValues.tenantId,
      this.importadoWowve
    );
    client.segmentos = formValues.segmentos || [];
    client.planosSegmento = formValues.planosSegmento || [];
    client.clientePagadorId = formValues.clientePagadorId || null;

    const edicao = formValues.id;
    const request = edicao
      ? this.clientService.update(client)
      : this.clientService.save(client);

    request.subscribe((response) => {
      this.snackBar.open(edicao ? 'Cliente alterado com sucesso!' : 'Cliente salvo com sucesso!', 'Sucesso', {
        duration: 2000
      });
      const idAtualizado = response?.id || edicao;
      if (idAtualizado) {
        this.carregar(+idAtualizado);
      }
    }, errorResponse => {
      this.snackBar.open(errorResponse.error?.message, 'ERRO', { duration: 2000 });
    });
  }

  montarFormulario() {
    this.formulario = this.formBilder.group({
      id: [null, Validators.nullValidator],
      nomeFantasia: [null, [Validators.minLength(3), Validators.maxLength(250)]],
      telephone: [null, Validators.nullValidator],
      address: [null, Validators.nullValidator],
      block: [null, Validators.nullValidator],
      batch: [null, Validators.nullValidator],
      complement: [null],
      observation: [null],
      documento: [null],
      razaoSocial: [null, Validators.nullValidator],
      ativo: [true, Validators.nullValidator],
      whatsapp: [null, Validators.nullValidator],
      email: [null, Validators.email],
      cep: [null, Validators.nullValidator],
      estado: [null, Validators.nullValidator],
      bairro: [null],
      cidade: [null, Validators.nullValidator],
      pessoaContato: [null, Validators.nullValidator],
      contato: [null],
      plano: [null, Validators.nullValidator],
      carencia: [0, [Validators.min(0), Validators.max(180)]],
      diaVencimento: [5],
      tenantId: [],
      segmentos: [[]],
      planosSegmento: this.formBilder.array([]),
      clientePagadorId: [null]
    });
  }

  get planosSegmentoArray(): FormArray {
    return this.formulario.get('planosSegmento') as FormArray;
  }

  /** Gera tenant no padrão: 32 caracteres alfanuméricos (ex: KXhr7WdO4fmIqFkH974XSsu8JVFjSmoY). */
  gerarTenantId(): void {
    if (this.tenantIdBloqueado) {
      this.snackBar.open('O TenantId já foi gerado e não pode ser alterado.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    const alfabeto = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const tamanho = 32;
    const bytes = new Uint8Array(tamanho);
    crypto.getRandomValues(bytes);
    let tenant = '';
    for (let i = 0; i < tamanho; i++) {
      tenant += alfabeto[bytes[i] % alfabeto.length];
    }
    this.formulario.controls.tenantId.setValue(tenant);
    this.formulario.controls.tenantId.markAsDirty();
  }

  private criarGrupoSegmento(segmento: string, plano: string | null, planoId: number | null,
    diaVencimento: number | null, carencia: number | null,
    colaboradorId: number | null, colaboradorNome: string | null): FormGroup {
    return this.formBilder.group({
      segmento: [segmento],
      plano: [plano],
      planoId: [planoId],
      diaVencimento: [diaVencimento ?? 5],
      carencia: [carencia ?? 0],
      colaboradorId: [colaboradorId ?? null],
      colaboradorNome: [colaboradorNome ?? null],
    });
  }

  onSegmentosChange(): void {
    const selecionados: string[] = this.formulario.get('segmentos')?.value || [];
    const atuais = this.planosSegmentoArray.value as any[];
    this.sincronizarPlanosSegmento(selecionados, atuais);
  }

  private sincronizarPlanosSegmento(selecionados: string[], existentes: any[], fallback?: any): void {
    const arr = this.planosSegmentoArray;
    arr.clear();
    selecionados.forEach(seg => {
      const found = (existentes || []).find(e => e.segmento === seg);
      const plano = found?.plano ?? fallback?.plano ?? null;
      const planoId = found?.planoId ?? fallback?.planoId ?? null;
      const diaVencimento = found?.diaVencimento ?? fallback?.diaVencimento ?? null;
      const carencia = found?.carencia ?? fallback?.carencia ?? null;
      const colaboradorId = found?.colaboradorId ?? null;
      const colaboradorNome = found?.colaboradorNome ?? null;
      arr.push(this.criarGrupoSegmento(seg, plano, planoId, diaVencimento, carencia, colaboradorId, colaboradorNome));
      this.carregarPlanosDoSegmento(seg);
    });
  }

  private segmentoIdPorCodigo(codigo: string): number | null {
    const s = this.segmentos.find(x => (x.codigo || x.nome) === codigo);
    return s?.id ?? null;
  }

  carregarPlanosDoSegmento(segmento: string): void {
    const segmentoId = this.segmentoIdPorCodigo(segmento);
    if (segmentoId == null) {
      return;
    }
    this.planoService.buscarPlanosAtivos(segmentoId).subscribe(resp => {
      this.planosPorSegmento[segmento] = resp || [];
    });
  }

  private recarregarPlanosSegmentos(): void {
    this.planosSegmentoArray.controls.forEach(g => {
      const seg = g.get('segmento')?.value;
      if (seg) {
        this.carregarPlanosDoSegmento(seg);
      }
    });
  }

  onPlanoSegmentoChange(grupo: any): void {
    const seg = grupo.get('segmento')?.value;
    const planoId = grupo.get('planoId')?.value;
    const plano = (this.planosPorSegmento[seg] || []).find(p => p.id === planoId);
    grupo.get('plano')?.setValue(plano?.nome ?? null);
  }

  onColaboradorSegmentoChange(grupo: any): void {
    const colaboradorId = grupo.get('colaboradorId')?.value;
    const colaborador = this.colaboradores.find(c => c.id === colaboradorId);
    grupo.get('colaboradorNome')?.setValue(colaborador?.nome ?? null);
  }

  nomeSegmento(codigo: string): string {
    const s = this.segmentos.find(x => (x.codigo || x.nome) === codigo);
    return s?.nome || codigo;
  }

  imprimirContrato(grupo: FormGroup | any): void {
    const clienteId = this.formulario.get('id')?.value;
    const segmento = grupo?.get?.('segmento')?.value ?? grupo?.value?.segmento;
    const planoId = grupo?.get?.('planoId')?.value ?? grupo?.value?.planoId;

    if (!clienteId) {
      this.snackBar.open('Salve o cliente antes de imprimir o contrato.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    if (!planoId || !segmento) {
      this.snackBar.open('Selecione o plano do segmento antes de imprimir.', 'ATENÇÃO', { duration: 3000 });
      return;
    }

    this.mostrarLoading();
    this.relatorioService.contrato(clienteId, segmento).subscribe(
      (response: any) => {
        const novaAba = window.open('', '_blank');
        if (!novaAba) {
          const contentDisposition = response.headers.get('content-disposition');
          const fileName = contentDisposition?.split('filename=')[1]?.replace(/"/g, '')
            || `contrato-${segmento}.pdf`;
          const file = new Blob([response.body], { type: 'application/pdf' });
          const blob = window.URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = blob;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(blob);
          link.remove();
          this.fecharLoading();
          return;
        }
        const file = new Blob([response.body], { type: 'application/pdf' });
        const blob = window.URL.createObjectURL(file);
        novaAba.location.href = blob;
        this.fecharLoading();
        setTimeout(() => window.URL.revokeObjectURL(blob), 10000);
      },
      (err) => {
        this.snackBar.open(
          err.error?.message || 'Não foi possível gerar o contrato. Salve o cliente e tente novamente.',
          'Fechar',
          { duration: 4000, panelClass: ['snack-error'] }
        );
        this.fecharLoading();
      }
    );
  }

  limparFormulario() {
    this.router.navigate(['/client/form']);
    this.formulario.reset();
    this.cidades = [];
    this.importadoWowve = false;
    this.ativo = false;
  }

  voltarConsulta() {
    this.router.navigate(['/client/consulta']);
  }

  onEstadoChange(estado: EstadosBrasil) {
    this.buscarMunicipios(this.getSiglaEstado(estado));
    this.formulario.controls.cidade.setValue(null);
  }

  getSiglaEstado(estado: EstadosBrasil): string {
    return NomesEstadosBrasil.get(estado) ?? '';
  }

  buscarDadosCep(): void {
    this.mostrarLoading();
    if (!this.utils.isNullOrEmpty(this.formulario.get('cep')?.value)) {
      this.viaCepService.buscarCep(this.formulario.get('cep')?.value).subscribe(resposta => {
        this.formulario.controls['estado']!.setValue(resposta.estado);
        this.formulario.controls['cidade']!.setValue(resposta.localidade);
        this.formulario.controls['bairro']!.setValue(resposta.bairro);
        this.formulario.controls['address']!.setValue(resposta.logradouro);
        this.buscarMunicipios(this.buscarSiglaEstadoInformado(resposta.estado));
        this.fecharLoading();
      }, () => {
        this.formulario.controls['estado']!.setValue('');
        this.formulario.controls['cidade']!.setValue('');
        this.formulario.controls['bairro']!.setValue('');
        this.formulario.controls['address']!.setValue('');
        this.cidades = [];
        this.fecharLoading();
      });
    } else {
      this.formulario.controls['estado']!.setValue('');
      this.formulario.controls['cidade']!.setValue('');
      this.formulario.controls['bairro']!.setValue('');
      this.formulario.controls['address']!.setValue('');
      this.cidades = [];
      this.fecharLoading();
    }
  }

  buscarMunicipios(uf: string): void {
    if (!this.utils.isNullOrEmpty(uf)) {
      this.cidadeService.buscarMunicipios(uf).subscribe(resposta => {
        this.cidades = (resposta || []).map((item: any) =>
          typeof item === 'string' ? item : (item?.nome ?? item?.cidade ?? '')
        );
      });
    } else {
      this.cidades = [];
    }
  }

  buscarSiglaEstadoInformado(estado: string): string {
    if (!this.utils.isNullOrEmpty(estado)) {
      const sigla = Object.entries(EstadosBrasil).find(
        ([, nome]) => nome.toLowerCase() === estado.toLowerCase()
      )?.[0];
      return sigla || '';
    }
    return '';
  }

  criarCadastroWowve(): void {
    if (this.importadoWowve) {
      this.snackBar.open('Cadastro WOWve já foi criado para este cliente!', 'Atenção', { duration: 4000 });
      return;
    }
    const idClient = this.formulario.get('id')?.value;
    if (!idClient) {
      this.snackBar.open('Cliente não identificado', 'OK', { duration: 3000 });
      return;
    }
    if (this.formulario.invalid) {
      this.snackBar.open('Formulário inválido', 'OK', { duration: 3000 });
      return;
    }
    this.clientService.criarCadastroWowve(idClient).subscribe(() => {
      this.importadoWowve = true;
      this.submit();
    }, errorResponse => {
      const msg = errorResponse?.error?.mensagem || 'Erro ao criar cadastro na WOWve';
      this.snackBar.open(msg, 'OK', { duration: 4000 });
    });
  }

  alterarPlanoWowve(): void {
    const idClient = this.formulario.get('id')?.value;
    if (!idClient) {
      this.snackBar.open('Cliente não identificado', 'OK', { duration: 3000 });
      return;
    }
    if (this.formulario.invalid) {
      this.snackBar.open('Formulário inválido', 'OK', { duration: 3000 });
      return;
    }
    this.clientService.alterarPlanoWowve(idClient).subscribe(() => {
      this.submit();
    }, errorResponse => {
      const msg = errorResponse?.error?.mensagem || 'Erro ao alterar plano na WOWve';
      this.snackBar.open(msg, 'OK', { duration: 4000 });
    });
  }

  alterarStatusWowve(): void {
    if (this.formulario.invalid) {
      this.snackBar.open('Formulário inválido', 'OK', { duration: 3000 });
      return;
    }
    const idClient = this.formulario.get('id')?.value;
    if (!idClient) {
      return;
    }
    const statusAtual = this.formulario.get('ativo')?.value;
    const novoStatus = !statusAtual;
    this.formulario.patchValue({ ativo: novoStatus });
    this.ativo = novoStatus;
    this.clientService.alterarStatusWowve(idClient, novoStatus).subscribe(() => {
      this.submit();
    }, errorResponse => {
      this.formulario.patchValue({ ativo: statusAtual });
      this.ativo = statusAtual;
      const msg = errorResponse?.error?.mensagem || 'Empresa salva localmente, mas falhou ao sincronizar com WOWve';
      this.snackBar.open(msg, 'OK', { duration: 4000 });
    });
  }

  // ── Documentos ──────────────────────────────────────────────────────────────
  carregarDocumentos(id: number) {
    this.clientService.listarDocumentos(id).subscribe(lista => this.documentos = lista ?? []);
  }

  selecionarArquivo(input: HTMLInputElement) {
    if (!this.clienteId) {
      this.snackBar.open('Salve o cliente antes de anexar documentos.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    input.click();
  }

  onArquivoSelecionado(event: Event) {
    const input = event.target as HTMLInputElement;
    const arquivos = input.files ? Array.from(input.files) : [];
    if (!arquivos.length || !this.clienteId) {
      return;
    }
    this.enviandoDocumento = true;
    const envios = arquivos.map(arquivo => this.clientService.anexarDocumento(this.clienteId!, arquivo));
    forkJoin(envios).subscribe(() => {
      this.enviandoDocumento = false;
      input.value = '';
      const msg = arquivos.length === 1
        ? 'Documento anexado.'
        : `${arquivos.length} documentos anexados.`;
      this.snackBar.open(msg, 'OK', { duration: 2000 });
      this.carregarDocumentos(this.clienteId!);
    }, err => {
      this.enviandoDocumento = false;
      input.value = '';
      this.snackBar.open(err.error?.message ?? 'Erro ao anexar documento(s).', 'ERRO', { duration: 3000 });
      this.carregarDocumentos(this.clienteId!);
    });
  }

  baixarDocumento(doc: ClientDocumento) {
    if (!this.clienteId || !doc.id) {
      return;
    }
    this.clientService.baixarDocumento(this.clienteId, doc.id).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.nome || 'documento';
      link.target = '_blank';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }, () => this.snackBar.open('Erro ao abrir documento.', 'ERRO', { duration: 2500 }));
  }

  removerDocumento(doc: ClientDocumento) {
    if (!this.clienteId || !doc.id || !confirm('Remover este documento?')) {
      return;
    }
    this.clientService.removerDocumento(this.clienteId, doc.id).subscribe(() => {
      this.snackBar.open('Documento removido.', 'OK', { duration: 2000 });
      this.carregarDocumentos(this.clienteId!);
    }, () => this.snackBar.open('Erro ao remover documento.', 'ERRO', { duration: 2500 }));
  }

  formatarTamanho(bytes?: number): string {
    if (!bytes && bytes !== 0) {
      return '';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  iconeDocumento(doc: ClientDocumento): string {
    const mime = (doc.mimeType || '').toLowerCase();
    const nome = (doc.nome || '').toLowerCase();
    if (mime.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|bmp)$/.test(nome)) {
      return 'image';
    }
    if (mime.startsWith('video/') || /\.(mp4|webm|mov|avi|mkv)$/.test(nome)) {
      return 'videocam';
    }
    if (mime.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|aac)$/.test(nome)) {
      return 'audiotrack';
    }
    if (mime === 'application/pdf' || nome.endsWith('.pdf')) {
      return 'picture_as_pdf';
    }
    return 'description';
  }

  public mostrarLoading() {
    document.getElementById('preloader')?.setAttribute('style', 'display:block;');
  }

  public fecharLoading() {
    document.getElementById('preloader')?.setAttribute('style', 'display:none;');
  }
}
